import { validDeck, validMastery, type Action, type Kind } from '@orb/sim';
import { ProtocolError, Reader, Writer } from './codec.ts';
import { ACTIONS, C2S, KINDS, LEAVE_REASONS, MODES, ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, S2C, SKILL_IDS, TOTEM_KINDS, type LeaveReason, type Mode } from './constants.ts';

/* ---------------- message shapes ---------------- */

export interface PlayerInfo { seat: number; name: string; connected: boolean; bot: boolean }
/** One input inside a turn: which tick of the turn (0…ticksPerTurn−1), whose, and what. */
export interface TurnInput { offset: number; seat: number; action: Action }

export type ClientMessage =
  /**
   * `build`: the client's game rules (the sim's STATE_VERSION); only equal builds are matched together.
   * `token`: a game session to resume (a seat). `auth`: the account's session token from `/auth/*`.
   */
  | { type: 'hello'; version: number; build: number; token: string | null; auth?: string | null }
  | { type: 'create_room'; mode: Mode }
  | { type: 'join_room'; code: string }
  | { type: 'queue'; mode: Mode }
  | { type: 'leave' }
  | { type: 'input'; tick: number; action: Action }
  | { type: 'checksum'; tick: number; hash: number }
  | { type: 'ping'; time: number }
  | { type: 'snapshot'; tick: number; part: number; total: number; data: Uint8Array }
  | { type: 'resync'; fromTick: number }
  | { type: 'result'; tick: number; hash: number; kills: number[] };

export type ServerMessage =
  /** Answer to a good `hello`: the session token to keep for reconnecting. */
  | { type: 'welcome'; session: string; resumed: boolean }
  | { type: 'room'; code: string; seed: number; mode: Mode; seat: number; players: PlayerInfo[]; startTick: number }
  /** Inputs for ticks [fromTick, fromTick + ticks); sent even when empty. */
  | { type: 'turn'; fromTick: number; ticks: number; inputs: TurnInput[] }
  | { type: 'player_joined'; player: PlayerInfo }
  | { type: 'player_left'; seat: number; reason: LeaveReason }
  | { type: 'snapshot_request'; tick: number }
  | { type: 'snapshot'; tick: number; part: number; total: number; data: Uint8Array }
  | { type: 'desync'; tick: number }
  | { type: 'pong'; time: number; serverTime: number }
  | { type: 'error'; code: number }
  /** `accepted`: the claim matched the server's replay. The tally is always the server's own. */
  | { type: 'verdict'; tick: number; accepted: boolean; kills: number[]; bossKills: number[]; leaks: number }
  /** A random match is ready in room `code` on another instance: reconnect via `routedUrl` and join it. */
  | { type: 'match'; code: string };

/* ---------------- actions: ≤ 6 bytes ---------------- */

/** Centimetres in a signed 16-bit field: ±327 m, exactly the sim's own input precision. */
const toCm = (v: number) => {
  const cm = Math.round(v * 100);
  if (!Number.isFinite(cm) || cm < -32768 || cm > 32767) throw new ProtocolError('coordinate out of range');
  return cm;
};

function writeAction(w: Writer, a: Action) {
  w.u8(ACTIONS.indexOf(a.type));
  switch (a.type) {
    case 'summon': break;
    case 'merge': w.u8(a.from).u8(a.to); break;
    case 'cast': w.u8(SKILL_IDS.indexOf(a.skill)).i16(toCm(a.x)).i16(toCm(a.z)); break;
    case 'equip_totem': w.u8(TOTEM_KINDS.indexOf(a.kind)); break;
    case 'equip_skill': w.u8(SKILL_IDS.indexOf(a.skill)); break;
    case 'shatter': w.u8(a.cell); break;
    // the deck as a bit per kind, then (if sent) a mastery byte per picked sphere, both in KINDS order
    case 'set_deck': {
      const order = KINDS.filter((k) => a.kinds.includes(k));
      w.u16(order.reduce((bits, k) => bits | (1 << KINDS.indexOf(k)), 0)).u8(a.mastery ? 1 : 0);
      if (a.mastery) for (const k of order) w.u8(a.mastery[a.kinds.indexOf(k)]);
      break;
    }
  }
}

function readAction(r: Reader): Action {
  const type = r.index(ACTIONS, 'action');
  switch (type) {
    case 'summon': return { type };
    case 'merge': return { type, from: r.u8(), to: r.u8() };
    case 'cast': return { type, skill: r.index(SKILL_IDS, 'skill'), x: r.i16() / 100, z: r.i16() / 100 };
    case 'equip_totem': return { type, kind: r.index(TOTEM_KINDS, 'totem') };
    case 'equip_skill': return { type, skill: r.index(SKILL_IDS, 'skill') };
    case 'shatter': return { type, cell: r.u8() };
    case 'set_deck': {
      const bits = r.u16(), kinds = KINDS.filter((_, i) => bits & (1 << i)) as Kind[];
      return r.u8() ? { type, kinds, mastery: kinds.map(() => r.u8()) } : { type, kinds };
    }
  }
}

function validateAction(a: Action) {
  if (!ACTIONS.includes(a.type)) throw new ProtocolError('unknown action');
  if (a.type === 'merge' && !(isU8(a.from) && isU8(a.to))) throw new ProtocolError('bad merge cells');
  if (a.type === 'cast' && !SKILL_IDS.includes(a.skill)) throw new ProtocolError('unknown skill');
  if (a.type === 'equip_totem' && !TOTEM_KINDS.includes(a.kind)) throw new ProtocolError('unknown totem');
  if (a.type === 'equip_skill' && !SKILL_IDS.includes(a.skill)) throw new ProtocolError('unknown skill');
  if (a.type === 'shatter' && !isU8(a.cell)) throw new ProtocolError('bad shatter cell');
  if (a.type === 'set_deck' && !(Array.isArray(a.kinds) && validDeck(a.kinds) && validMastery(a.mastery, a.kinds.length))) throw new ProtocolError('bad deck');
}

/* ---------------- helpers ---------------- */

const isU8 = (v: number) => Number.isInteger(v) && v >= 0 && v <= 255;
const isU16 = (v: number) => Number.isInteger(v) && v >= 0 && v <= 65535;
const isU32 = (v: number) => Number.isInteger(v) && v >= 0 && v <= 4294967295;
function check(ok: boolean, what: string) { if (!ok) throw new ProtocolError(`bad ${what}`); }

function writeCode(w: Writer, code: string) {
  check(code.length === ROOM_CODE_LENGTH && [...code].every((c) => ROOM_CODE_ALPHABET.includes(c)), 'room code');
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) w.u8(code.charCodeAt(i));
}
function readCode(r: Reader): string {
  let code = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) code += String.fromCharCode(r.u8());
  check([...code].every((c) => ROOM_CODE_ALPHABET.includes(c)), 'room code');
  return code;
}
function writePlayer(w: Writer, p: PlayerInfo) { check(isU8(p.seat), 'seat'); w.u8(p.seat).u8((p.connected ? 1 : 0) | (p.bot ? 2 : 0)).str(p.name); }
function readPlayer(r: Reader): PlayerInfo {
  const seat = r.u8(), flags = r.u8();
  check(flags < 4, 'player flags');
  return { seat, connected: !!(flags & 1), bot: !!(flags & 2), name: r.str() };
}
function writeSnapshot(w: Writer, m: { tick: number; part: number; total: number; data: Uint8Array }) {
  check(isU32(m.tick), 'tick'); check(isU16(m.total) && m.total > 0 && isU16(m.part) && m.part < m.total, 'snapshot part');
  check(m.data.length <= 65535, 'snapshot part size');
  w.u32(m.tick).u16(m.part).u16(m.total).u16(m.data.length).bytes(m.data);
}
function readSnapshot(r: Reader) {
  const tick = r.u32(), part = r.u16(), total = r.u16();
  check(total > 0 && part < total, 'snapshot part');
  return { tick, part, total, data: r.bytes(r.u16()) };
}

/** A per-seat tally: u8 count, then u32 each. */
function writeCounts(w: Writer, xs: number[]) { check(xs.length <= 8 && xs.every(isU32), 'counts'); w.u8(xs.length); for (const x of xs) w.u32(x); }
function readCounts(r: Reader): number[] { const n = r.u8(); check(n <= 8, 'counts'); return Array.from({ length: n }, () => r.u32()); }

/* ---------------- client → server ---------------- */

export function encodeClient(m: ClientMessage): Uint8Array {
  const w = new Writer();
  w.u8(C2S[m.type]);
  switch (m.type) {
    case 'hello': check(isU16(m.version) && isU16(m.build), 'version'); w.u16(m.version).u16(m.build).str(m.token ?? '').str(m.auth ?? ''); break;
    case 'create_room': case 'queue': check(MODES.includes(m.mode), 'mode'); w.u8(MODES.indexOf(m.mode)); break;
    case 'join_room': writeCode(w, m.code); break;
    case 'leave': break;
    case 'input': check(isU32(m.tick), 'tick'); validateAction(m.action); w.u32(m.tick); writeAction(w, m.action); break;
    case 'checksum': check(isU32(m.tick) && isU32(m.hash), 'checksum'); w.u32(m.tick).u32(m.hash); break;
    case 'ping': check(Number.isFinite(m.time), 'time'); w.f64(m.time); break;
    case 'snapshot': writeSnapshot(w, m); break;
    case 'resync': check(isU32(m.fromTick), 'tick'); w.u32(m.fromTick); break;
    case 'result': check(isU32(m.tick) && isU32(m.hash), 'result'); writeCounts(w, m.kills); w.u32(m.tick).u32(m.hash); break;
  }
  return w.finish();
}

/** Strict: unknown codes, out-of-range enums, truncation and trailing bytes all throw ProtocolError. */
export function decodeClient(data: Uint8Array): ClientMessage {
  const r = new Reader(data);
  const code = r.u8();
  let m: ClientMessage;
  switch (code) {
    case C2S.hello: { const version = r.u16(), build = r.u16(), token = r.str(), auth = r.str(); m = { type: 'hello', version, build, token: token || null, auth: auth || null }; break; }
    case C2S.create_room: m = { type: 'create_room', mode: r.index(MODES, 'mode') }; break;
    case C2S.join_room: m = { type: 'join_room', code: readCode(r) }; break;
    case C2S.queue: m = { type: 'queue', mode: r.index(MODES, 'mode') }; break;
    case C2S.leave: m = { type: 'leave' }; break;
    case C2S.input: m = { type: 'input', tick: r.u32(), action: readAction(r) }; break;
    case C2S.checksum: m = { type: 'checksum', tick: r.u32(), hash: r.u32() }; break;
    case C2S.ping: m = { type: 'ping', time: r.f64() }; break;
    case C2S.snapshot: m = { type: 'snapshot', ...readSnapshot(r) }; break;
    case C2S.resync: m = { type: 'resync', fromTick: r.u32() }; break;
    case C2S.result: { const kills = readCounts(r); m = { type: 'result', kills, tick: r.u32(), hash: r.u32() }; break; }
    default: throw new ProtocolError(`unknown client message ${code}`);
  }
  r.end();
  return m;
}

/* ---------------- server → client ---------------- */

export function encodeServer(m: ServerMessage): Uint8Array {
  const w = new Writer();
  w.u8(S2C[m.type]);
  switch (m.type) {
    case 'welcome': w.str(m.session).u8(m.resumed ? 1 : 0); break;
    case 'room':
      writeCode(w, m.code); check(isU32(m.seed) && isU8(m.seat) && isU32(m.startTick) && m.players.length <= 255 && MODES.includes(m.mode), 'room');
      w.u32(m.seed).u8(MODES.indexOf(m.mode)).u8(m.seat).u32(m.startTick).u8(m.players.length);
      for (const p of m.players) writePlayer(w, p);
      break;
    case 'turn':
      check(isU32(m.fromTick) && isU8(m.ticks) && m.ticks > 0 && m.inputs.length <= 255, 'turn');
      w.u32(m.fromTick).u8(m.ticks).u8(m.inputs.length);
      for (const i of m.inputs) {
        check(isU8(i.offset) && i.offset < m.ticks && isU8(i.seat), 'turn input');
        validateAction(i.action);
        w.u8(i.offset).u8(i.seat); writeAction(w, i.action);
      }
      break;
    case 'player_joined': writePlayer(w, m.player); break;
    case 'player_left': check(isU8(m.seat) && LEAVE_REASONS.includes(m.reason), 'player_left'); w.u8(m.seat).u8(LEAVE_REASONS.indexOf(m.reason)); break;
    case 'snapshot_request': case 'desync': check(isU32(m.tick), 'tick'); w.u32(m.tick); break;
    case 'snapshot': writeSnapshot(w, m); break;
    case 'pong': check(Number.isFinite(m.time) && Number.isFinite(m.serverTime), 'pong'); w.f64(m.time).f64(m.serverTime); break;
    case 'error': check(isU16(m.code), 'error code'); w.u16(m.code); break;
    case 'match': writeCode(w, m.code); break;
    case 'verdict': check(isU32(m.tick) && isU32(m.leaks), 'verdict'); w.u32(m.tick).u8(m.accepted ? 1 : 0).u32(m.leaks); writeCounts(w, m.kills); writeCounts(w, m.bossKills); break;
  }
  return w.finish();
}

export function decodeServer(data: Uint8Array): ServerMessage {
  const r = new Reader(data);
  const code = r.u8();
  let m: ServerMessage;
  switch (code) {
    case S2C.welcome: { const session = r.str(), flag = r.u8(); check(flag < 2, 'welcome flag'); m = { type: 'welcome', session, resumed: flag === 1 }; break; }
    case S2C.room: {
      const roomCode = readCode(r), seed = r.u32(), mode = r.index(MODES, 'mode'), seat = r.u8(), startTick = r.u32();
      const players = Array.from({ length: r.u8() }, () => readPlayer(r));
      m = { type: 'room', code: roomCode, seed, mode, seat, startTick, players };
      break;
    }
    case S2C.turn: {
      const fromTick = r.u32(), ticks = r.u8();
      check(ticks > 0, 'turn length');
      const inputs = Array.from({ length: r.u8() }, (): TurnInput => {
        const offset = r.u8(); check(offset < ticks, 'turn offset');
        return { offset, seat: r.u8(), action: readAction(r) };
      });
      m = { type: 'turn', fromTick, ticks, inputs };
      break;
    }
    case S2C.player_joined: m = { type: 'player_joined', player: readPlayer(r) }; break;
    case S2C.player_left: m = { type: 'player_left', seat: r.u8(), reason: r.index(LEAVE_REASONS, 'leave reason') }; break;
    case S2C.snapshot_request: m = { type: 'snapshot_request', tick: r.u32() }; break;
    case S2C.snapshot: m = { type: 'snapshot', ...readSnapshot(r) }; break;
    case S2C.desync: m = { type: 'desync', tick: r.u32() }; break;
    case S2C.pong: m = { type: 'pong', time: r.f64(), serverTime: r.f64() }; break;
    case S2C.error: m = { type: 'error', code: r.u16() }; break;
    case S2C.verdict: {
      const tick = r.u32(), flag = r.u8(); check(flag < 2, 'verdict flag');
      m = { type: 'verdict', tick, accepted: flag === 1, leaks: r.u32(), kills: readCounts(r), bossKills: readCounts(r) };
      break;
    }
    case S2C.match: m = { type: 'match', code: readCode(r) }; break;
    default: throw new ProtocolError(`unknown server message ${code}`);
  }
  r.end();
  return m;
}
